import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { FormLangTab } from 'Components/Tabs'
import { InputUI, TextEditorUI } from 'Components/UI'
import { formLangTab } from 'Data/app'
import { PlusIcon } from 'Icons/Plus'
import { useEffect, useMemo, useRef, useState } from 'react'
import { LangError } from 'Types/app'
import { AnnouncementSchoolOption, SchoolAnnouncement, SchoolAnnouncementPayload } from '../types'

type SubmitValues = {
  payload: SchoolAnnouncementPayload
  files: File[]
  removedAttachmentIds: number[]
}

type Props = {
  open: boolean
  data?: SchoolAnnouncement | null
  isAdmin?: boolean
  isLoading?: boolean
  schools?: AnnouncementSchoolOption[]
  onClose: () => void
  onSubmit: (values: SubmitValues) => void
}

const initialForm: SchoolAnnouncementPayload = {
  titleUz: '',
  titleRu: '',
  titleKo: '',
  contentUz: '',
  contentRu: '',
  contentKo: '',
}

const langFieldMap = {
  Uz: { title: 'titleUz', content: 'contentUz' },
  Ru: { title: 'titleRu', content: 'contentRu' },
  Kr: { title: 'titleKo', content: 'contentKo' },
} as const

const getFileSize = (size: number) => {
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`
  return `${(size / 1024 / 1024).toFixed(1)} MB`
}

export const SchoolAnnouncementDialog = ({ open, data, isAdmin, isLoading, schools = [], onClose, onSubmit }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [form, setForm] = useState<SchoolAnnouncementPayload>(initialForm)
  const [schoolId, setSchoolId] = useState<number>(0)
  const [files, setFiles] = useState<File[]>([])
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([])
  const [submitAttempted, setSubmitAttempted] = useState(false)

  useEffect(() => {
    if (!open) return

    setForm(
      data
        ? {
            titleUz: data.titleUz ?? '',
            titleRu: data.titleRu ?? '',
            titleKo: data.titleKo ?? '',
            contentUz: data.contentUz ?? '',
            contentRu: data.contentRu ?? '',
            contentKo: data.contentKo ?? '',
          }
        : initialForm,
    )
    setSchoolId(data?.school?.schoolId ?? data?.schoolId ?? 0)
    setFiles([])
    setRemovedAttachmentIds([])
    setSubmitAttempted(false)
  }, [data, open])

  const errors = useMemo(
    () => ({
      titleUz: !form.titleUz.trim(),
      titleRu: !form.titleRu.trim(),
      titleKo: !form.titleKo.trim(),
      contentUz: !form.contentUz.trim(),
      contentRu: !form.contentRu.trim(),
      contentKo: !form.contentKo.trim(),
    }),
    [form],
  )

  const langError = useMemo<Partial<LangError>>(
    () => ({
      Uz: errors.titleUz || errors.contentUz,
      Ru: errors.titleRu || errors.contentRu,
      Kr: errors.titleKo || errors.contentKo,
    }),
    [errors],
  )

  const hasError = Object.values(errors).some(Boolean)

  const handleChange = (field: keyof SchoolAnnouncementPayload) => (value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files ?? [])
    setFiles(prev => [...prev, ...nextFiles])
    event.target.value = ''
  }

  const handleRemoveAttachment = (attachmentId: number) => {
    setRemovedAttachmentIds(prev => (prev.includes(attachmentId) ? prev : [...prev, attachmentId]))
  }

  const handleSubmit = () => {
    setSubmitAttempted(true)
    if (hasError) return

    onSubmit({
      payload: {
        ...(isAdmin && schoolId ? { schoolId } : {}),
        titleUz: form.titleUz.trim(),
        titleRu: form.titleRu.trim(),
        titleKo: form.titleKo.trim(),
        contentUz: form.contentUz.trim(),
        contentRu: form.contentRu.trim(),
        contentKo: form.contentKo.trim(),
      },
      files,
      removedAttachmentIds,
    })
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg' disableEnforceFocus>
      <DialogTitle>{data ? 'Редактировать объявление' : 'Создать объявление'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={0.5}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant='body2' color='text.secondary'>
              Заполните заголовок и текст на узбекском, русском и корейском. Вложения будут загружены после сохранения объявления.
            </Typography>
          </Paper>

          {isAdmin ? (
            <TextField
              select
              fullWidth
              disabled={!!data}
              label='Школа'
              value={schoolId || ''}
              onChange={event => setSchoolId(Number(event.target.value))}
              helperText={
                data
                  ? 'При редактировании существующего объявления школу изменить нельзя.'
                  : 'При необходимости выберите школу, к которой относится объявление.'
              }>
              <MenuItem value=''>Без привязки к школе</MenuItem>
              {schools.map(school => (
                <MenuItem key={school.schoolId} value={school.schoolId}>
                  {school.schoolNameRu || school.schoolNameUz || school.schoolNameKo} · {school.regionName}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          <FormLangTab
            error={submitAttempted ? langError : {}}
            render={lang => {
              const fields = langFieldMap[lang as keyof typeof langFieldMap]
              const titleField = fields.title
              const contentField = fields.content

              return (
                <Grid container rowSpacing={3}>
                  {formLangTab.map(({ id }) => {
                    if (lang !== id) return null

                    return (
                      <Grid item xs={12} key={id}>
                        <Stack spacing={3}>
                          <InputUI
                            label={`Заголовок (${id === 'Kr' ? 'Ko' : id})`}
                            placeholder='Введите заголовок объявления'
                            value={form[titleField]}
                            onChange={event => handleChange(titleField)(event.target.value)}
                            error={submitAttempted && !!errors[titleField]}
                            helperText={submitAttempted && errors[titleField] ? 'Заголовок обязателен.' : undefined}
                          />

                          <TextEditorUI
                            label={`Текст (${id === 'Kr' ? 'Ko' : id})`}
                            value={form[contentField]}
                            onChange={handleChange(contentField)}
                            errorText={submitAttempted && errors[contentField] ? 'Текст обязателен.' : ''}
                          />
                        </Stack>
                      </Grid>
                    )
                  })}
                </Grid>
              )
            }}
          />

          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Stack spacing={2}>
              <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
                <Box>
                  <Typography variant='subtitle1' fontWeight={700}>
                    Вложения
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Добавьте изображения или документы, связанные с этим объявлением.
                  </Typography>
                </Box>
                <Button variant='outlined' startIcon={<PlusIcon />} onClick={() => inputRef.current?.click()}>
                  Добавить файлы
                </Button>
              </Stack>

              {data?.attachments?.some(item => !removedAttachmentIds.includes(item.id)) ? (
                <Stack direction='row' spacing={1} flexWrap='wrap'>
                  {data.attachments.map(item => {
                    if (removedAttachmentIds.includes(item.id)) return null

                    return (
                      <Chip
                        key={item.id}
                        label={item.originalFileName}
                        component='a'
                        href={item.path}
                        target='_blank'
                        clickable
                        variant='outlined'
                        onDelete={event => {
                          event.preventDefault()
                          handleRemoveAttachment(item.id)
                        }}
                      />
                    )
                  })}
                </Stack>
              ) : null}

              {files.length ? (
                <Stack spacing={1}>
                  {files.map((file, index) => (
                    <Stack
                      key={`${file.name}-${index}`}
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                      sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: '#F7F7F7' }}>
                      <Box>
                        <Typography fontWeight={600}>{file.name}</Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {getFileSize(file.size)}
                        </Typography>
                      </Box>
                      <Button color='secondary' onClick={() => setFiles(prev => prev.filter((_, fileIndex) => fileIndex !== index))}>
                        Удалить
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Новые файлы не выбраны.
                </Typography>
              )}
            </Stack>

            <input ref={inputRef} type='file' multiple onChange={handleFilesChange} style={{ display: 'none' }} />
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='inherit' onClick={onClose} disabled={isLoading}>
          Отмена
        </Button>
        <Button
          startIcon={isLoading ? <CircularProgress size={18} color='inherit' /> : undefined}
          disabled={isLoading}
          variant='contained'
          onClick={handleSubmit}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  )
}
